import React from "react";
import {
  alpha,
  Box,
  Chip,
  Drawer as MuiDrawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { 
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  MoneyOff as MoneyOffIcon,
  AttachMoney as AttachMoneyIcon,
  Category as CategoryIcon,
  Assessment as AssessmentIcon,
  Info as InfoIcon,
  AccountBalanceWallet,
  Feedback as FeedbackIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { Drawer } from "../../styles/DrawerStyle";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ABOUT_ROUTE_PATH,
  CATEGORIES_ROUTE_PATH,
  EXPENSES_ROUTE_PATH,
  HOME_ROUTE_PATH,
  INCOMES_ROUTE_PATH,
  MONTHLY_REPORTS_ROUTE_PATH,
  CONTACT_ROUTE_PATH,
} from "../../utils/constants/AppConstants";
import { useAuth } from "../../auth/AuthContext";

interface SidebarMenuComponentProps {
  mobileMenuOpen?: boolean;
  onMobileMenuClose?: () => void;
}

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

interface NavSection {
  section: string;
  items: NavItem[];
}

const AUTH_DESKTOP_SECTIONS: NavSection[] = [
  {
    section: "Overview",
    items: [
      { text: "Dashboard", icon: <HomeIcon />, path: HOME_ROUTE_PATH },
    ],
  },
  {
    section: "Finance",
    items: [
      { text: "Expenses", icon: <MoneyOffIcon />, path: EXPENSES_ROUTE_PATH },
      { text: "Income", icon: <AttachMoneyIcon />, path: INCOMES_ROUTE_PATH },
      { text: "Categories", icon: <CategoryIcon />, path: CATEGORIES_ROUTE_PATH },
    ],
  },
  {
    section: "Analytics",
    items: [
      { text: "Reports", icon: <AssessmentIcon />, path: MONTHLY_REPORTS_ROUTE_PATH },
    ],
  },
  {
    section: "Help",
    items: [
      { text: "About", icon: <InfoIcon />, path: ABOUT_ROUTE_PATH },
      { text: "Contact", icon: <FeedbackIcon />, path: CONTACT_ROUTE_PATH },
    ],
  },
];

const AUTH_MOBILE_SECTIONS: NavSection[] = [
  {
    section: "More",
    items: [
      { text: "About", icon: <InfoIcon />, path: ABOUT_ROUTE_PATH },
      { text: "Contact", icon: <FeedbackIcon />, path: CONTACT_ROUTE_PATH },
    ],
  },
];

const GUEST_SECTIONS: NavSection[] = [
  {
    section: "Welcome",
    items: [
      { text: "Home", icon: <HomeIcon />, path: HOME_ROUTE_PATH },
      { text: "About", icon: <InfoIcon />, path: ABOUT_ROUTE_PATH },
    ],
  },
];

const SidebarMenuComponent = ({ mobileMenuOpen = false, onMobileMenuClose }: SidebarMenuComponentProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const DRAWER_WIDTH = 296;
  const COLLAPSED_WIDTH = 76;
  const [open, setOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  const handleDrawerToggle = () => {
    setOpen((prev) => !prev);
  };

  const isActive = (path: string) => location.pathname === path;
  const showLabels = isMobile || open;
  const isDesktop = !isMobile;

  const navigationItems: NavSection[] = isAuthenticated
    ? isMobile
      ? AUTH_MOBILE_SECTIONS
      : AUTH_DESKTOP_SECTIONS
    : GUEST_SECTIONS;

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const handleMobileClose = () => {
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
      return;
    }
    handleDrawerToggle();
  };

  const drawerContent = (
    <>
      {isMobile && (
        <Box sx={{ pt: 1, display: "flex", justifyContent: "center", background: theme.palette.background.paper }}>
          <Box sx={{ width: 42, height: 4, borderRadius: 999, bgcolor: alpha(theme.palette.text.secondary, 0.35) }} />
        </Box>
      )}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: showLabels ? "space-between" : "center",
          p: showLabels ? 2 : 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.96)}, ${alpha(theme.palette.background.default, 0.9)})`,
        }}
      >
        {showLabels && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceWallet sx={{ color: theme.palette.primary.main, fontSize: "1.35rem" }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {isMobile ? "More" : "PocketBalance"}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {isMobile ? "Secondary actions" : "Navigation"}
              </Typography>
            </Box>
          </Box>
        )}

        {!showLabels && <AccountBalanceWallet sx={{ color: theme.palette.primary.main, fontSize: "1.25rem" }} />}

        <IconButton
          onClick={handleMobileClose}
          sx={{
            color: theme.palette.text.secondary,
            ml: showLabels ? 0 : "auto",
            border: `1px solid ${alpha(theme.palette.text.secondary, 0.2)}`,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
              color: theme.palette.primary.main,
              borderColor: alpha(theme.palette.primary.main, 0.35),
            },
            transition: "all 0.2s ease",
          }}
          aria-label={showLabels ? "Collapse navigation" : "Expand navigation"}
        >
          {showLabels ? (isMobile ? <CloseIcon /> : <ChevronLeftIcon />) : <ChevronRightIcon />}
        </IconButton>
      </Box>

      {/* Navigation sections */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1.25, px: isDesktop && showLabels ? 0.5 : 0 }}>
        {navigationItems.map((section, sectionIndex) => (
          <Box key={section.section} sx={{ mb: 1.75 }}>
            {showLabels && (
              <Typography
                variant="caption"
                sx={{
                  px: 2.5,
                  py: 0.8,
                  display: "block",
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontSize: "0.68rem",
                }}
              >
                {section.section}
              </Typography>
            )}

            <List sx={{ py: 0 }}>
              {section.items.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ px: 1 }}>
                  <Tooltip title={showLabels ? "" : item.text} placement="right" arrow>
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        minHeight: isMobile ? 52 : 46,
                        borderRadius: isMobile ? 2.25 : 1.75,
                        mx: isMobile ? 0.5 : 0.75,
                        mb: 0.5,
                        justifyContent: showLabels ? "initial" : "center",
                        px: showLabels ? (isMobile ? 2 : 1.75) : 1,
                        color: isActive(item.path) ? theme.palette.primary.light : theme.palette.text.primary,
                        border: isActive(item.path)
                          ? `1px solid ${alpha(theme.palette.primary.main, 0.45)}`
                          : "1px solid transparent",
                        backgroundColor: isActive(item.path)
                          ? alpha(theme.palette.primary.main, 0.14)
                          : "transparent",
                        position: "relative",
                        "&::before": isDesktop && showLabels
                          ? {
                              content: '""',
                              position: "absolute",
                              left: 8,
                              top: 10,
                              bottom: 10,
                              width: 3,
                              borderRadius: 999,
                              backgroundColor: isActive(item.path)
                                ? theme.palette.primary.main
                                : "transparent",
                            }
                          : {},
                        "&:hover": {
                          backgroundColor: isActive(item.path)
                            ? alpha(theme.palette.primary.main, 0.22)
                            : alpha(theme.palette.common.white, 0.04),
                          transform: isDesktop ? "translateX(2px)" : "none",
                        },
                        transition: "all 0.2s ease",
                        "& .MuiListItemIcon-root": {
                          color: isActive(item.path)
                            ? theme.palette.primary.light
                            : theme.palette.text.secondary,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: showLabels ? 1.6 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {showLabels && (
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: "0.9rem",
                            fontWeight: isActive(item.path) ? 600 : 500,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>

            {sectionIndex < navigationItems.length - 1 && showLabels && (
              <Divider sx={{ mx: 2, my: 0.75, borderColor: alpha(theme.palette.text.primary, 0.08) }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Footer */}
      {showLabels && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            background: alpha(theme.palette.background.paper, 0.86),
          }}
        >
          <Chip
            size="small"
            label={isMobile ? "PocketBalance • More" : "PocketBalance v1.0"}
            sx={{
              width: "100%",
              borderRadius: 999,
              color: theme.palette.text.secondary,
              backgroundColor: alpha(theme.palette.common.white, 0.04),
              border: `1px solid ${alpha(theme.palette.text.secondary, 0.16)}`,
            }}
          />
        </Box>
      )}
    </>
  );

  return (
    <>
      {/* Mobile Drawer - Temporary */}
      {isMobile ? (
        <MuiDrawer
          anchor="bottom"
          variant="temporary"
          open={mobileMenuOpen}
          onClose={onMobileMenuClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            "& .MuiDrawer-paper": {
              width: "100%",
              maxHeight: "72dvh",
              background: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
              overflowX: "hidden",
            },
          }}
        >
          {drawerContent}
        </MuiDrawer>
      ) : (
        /* Desktop Drawer - Permanent */
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            display: { xs: 'none', md: 'block' },
            zIndex: theme.zIndex.drawer - 2,
            width: open ? DRAWER_WIDTH : COLLAPSED_WIDTH,
            transition: 'width 0.2s ease-in-out',
            "& .MuiDrawer-paper": {
              marginTop: "72px",
              height: "calc(100vh - 72px)",
              width: open ? DRAWER_WIDTH : COLLAPSED_WIDTH,
              background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.96)}, ${alpha(theme.palette.background.default, 0.92)})`,
              borderRight: `1px solid ${theme.palette.divider}`,
              overflowX: "hidden",
              boxShadow: `inset -1px 0 0 ${alpha(theme.palette.common.white, 0.03)}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
      
    </>
  );
};

export default SidebarMenuComponent;
